import { Directive, EventEmitter, Input, Output } from '@angular/core';
import {
  ApplyColumnStateParams,
  ColDef,
  DefaultMenuItem,
  FilterModel,
  GetContextMenuItems,
  GetRowIdParams,
  GridApi,
  GridState,
  ICellRendererParams,
  IRowNode,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  MenuItemDef,
  RowClassRules,
  RowSelectedEvent,
  SelectionChangedEvent,
  SideBarDef
} from 'ag-grid-enterprise';
import { Observable } from 'rxjs';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';

import * as ModelClasses from '../../models';

@Directive()
export abstract class GridBase {
  @Input() gridName!: string;
  @Input() columnDefs?: ColDef[];
  @Input() defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    headerClass: 'floating-filter-test',
    autoHeaderHeight: true,
    wrapHeaderText: true,
    enableRowGroup: true,
    // VPDW-3111: brand status-column cells as colored chips. Only
    // fires for explicit status columns so boolean Yes/No cells and
    // free-text cells are left alone. The cellRenderer wraps the value
    // in a <span class="vive-status-chip vive-status-*"> which gets
    // styled in core.less.
    cellRenderer: (params: {
      column?: { getColId?: () => string };
      value?: unknown;
      valueFormatted?: unknown;
    }): string | null | undefined => {
      const raw = params?.valueFormatted ?? params?.value;
      if (raw === null || raw === undefined || raw === '') return '';
      const text = String(raw);
      const colId = params?.column?.getColId?.() || '';
      const looksLikeStatus =
        /(^|[A-Z])status|compliance|^result$|review$|registration|approval|sub-?status|delivery|payment-?status|paid$/i.test(
          colId
        );
      if (!looksLikeStatus) return text;
      const k = text.trim().toUpperCase();
      let tone = '';
      if (
        [
          'APPROVED',
          'ACTIVE',
          'ACTIVATED',
          'PASSED',
          'PASS',
          'PASSING',
          'CONFIRMED',
          'SENT',
          'PAID',
          'BASIC',
          'FULL',
          'BUSINESS VERIFIED',
          'SITE CONDITIONAL',
          // QuickSourceBids — bid was awarded to a vendor.
          'AWARDED',
          // Contracts — contract was renewed.
          'RENEWED',
          // AP / Comms sync status (ApVendor.syncStatus,
          // ApConfig.syncStatus, CommsConfig.syncStatus) — terminal
          // success state of a sync run, the counterpart to FAILED.
          'COMPLETED'
        ].includes(k)
      ) {
        tone = 'success';
      } else if (
        [
          'REGISTERED',
          'REGISTERING',
          'AWAITING PAYMENT',
          'REVIEWING',
          'REVIEW NEEDED',
          'REVIEW',
          'UNDER REVIEW',
          'MISSING',
          'EXEMPT',
          'EXEMPT - PASSED',
          'RELEASED',
          'PENDING',
          'RENEWING',
          'QUEUED',
          'IN_PROGRESS',
          // QuickSourceBids — bid drafted but not released yet.
          'CREATED',
          // Contracts — needs attention / not yet acted on.
          'NEW',
          // Contracts — expiring or renewing soon, needs action.
          'EXPIRES90',
          'EXPIRES30',
          'RENEW90',
          'RENEW30'
        ].includes(k)
      ) {
        tone = 'warning';
      } else if (
        [
          'SUSPENDED',
          'REJECTED',
          'DEACTIVATED',
          'EXPIRED',
          'FAILED',
          'FAIL',
          'CANCELLED',
          'UNLINKED',
          'EXEMPT - FAILED',
          'LICENSE ISSUES'
        ].includes(k)
      ) {
        tone = 'danger';
      } else if (
        [
          'INACTIVE',
          'INACTIVATED',
          'NON-VERIFIED',
          'UNREGISTERED',
          'CLOSED',
          'DRAFT',
          'NOT REQUIRED',
          'COMPLIANCE EXEMPT',
          // Contracts — archived state, no active work.
          'ARCHIVED'
        ].includes(k)
      ) {
        tone = 'muted';
      }
      if (!tone) return text;
      // Status values come from controlled DB enums — no XSS risk.
      // Escape just in case.
      const esc = text.replace(
        /[&<>"']/g,
        (c) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
          })[c] as string
      );
      return `<span class="vive-status-chip vive-status-${tone}">${esc}</span>`;
    }
  };
  @Input() rowData!: Observable<any[]>;
  @Input() rowSelectionType: 'single' | 'multiple' | undefined = 'single';
  @Input() rowClassRules?: RowClassRules;

  // Set to true to display a checkbox column.
  @Input() useCheckboxColumn: boolean = false;
  // Represents the name of the Primary Key for the passed Grid Data.
  @Input() gridPrimaryKeyFieldName?: string;
  // Represents a collection of primary keys which are used to select a grid row.
  @Input() selectedRowsPrimaryKeys: Set<string | number> = new Set();
  // Load data client-sided or server-sided.
  @Input() rowModelType: 'serverSide' | 'clientSide' = 'clientSide';
  // Apply external filters to a grid-query.
  @Input() Filters: Map<string, any> = new Map();
  // If row grouping is enabled, this sets how the grouping is handled/displayed by the grid.
  @Input() groupDisplayType:
    'singleColumn' | 'multipleColumns' | 'groupRows' | 'custom' =
    'singleColumn';
  // This is used to treat dot notation in field names as a literal, not as a deeply nested reference.
  @Input() suppressFieldDotNotation: boolean = true;
  // Enables pagination for server-side data. (Enabling this prevents infinite scroll and forces manual pagination
  // through a toolbar at the footer.)
  @Input() pagination: boolean = false;
  // Sets the number of rows to pull from server-side if pagination is enabled.
  @Input() paginationPageSize: number = 100;
  // Allows a specific grid configuration to be pulled from the backend.
  @Input() gridType!: keyof typeof ModelClasses;
  // Allows multiple rows to be selected with a single click, rather than forcing a user to hold Command/Ctrl
  // to select multiple rows.
  @Input() rowMultiSelectWithClick = true;
  // Prevents rows from being deselected on click.
  @Input() suppressRowClickSelection: boolean = false;
  // If selectionEnabled is set to false, all rows become unselectable/deselectable. If a row is already
  // selected, it stays selected.
  @Input() selectionEnabled: boolean = true;
  // Allows a grid cell to be selected and copied to clipboard.
  @Input() cellSelection: boolean = true;
  // Apply sorting to the grid from a parent component.
  @Input() SortState?: ApplyColumnStateParams;
  // Determines if all rows should be initially displayed as selected. Used for server side pagination.
  @Input() allRowsSelected?: boolean = false;

  @Input() sideBar: 'columns' | boolean | SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        minWidth: 225,
        maxWidth: 225,
        width: 225,
        toolPanelParams: {
          suppressPivotMode: true,
          suppressPivots: true
        }
      }
      // No need for duplicate filters. Floating column filters should suffice.
      // {
      //   id: 'filters',
      //   labelDefault: 'Filters',
      //   labelKey: 'filters',
      //   iconKey: 'filter',
      //   toolPanel: 'agFiltersToolPanel',
      //   minWidth: 180,
      //   maxWidth: 400,
      //   width: 250
      // }
    ],
    hiddenByDefault: false,
    position: 'right'
  } as SideBarDef;

  // This is used for 'action' columns (columns that contain icons acting as buttons).
  // We'll construct this map with a Column Id as the property and an array of string actions
  // as the value to apply to the column. The cell renderer applied to the action column's cells
  // will utilize this map to display and handle the icon buttons appropriately.
  @Input() ColumnIdToColumnActions?: GridActionColumnMap;

  // Tooltips to display for grid column actions.
  @Input() TooltipMap?: Partial<{ [key in GridActionColumnActions]: string }>;

  // Uses row data to determine action column items.
  @Input() getCalculatedColumnActions?: ({
    rowData
  }: {
    rowData: any;
  }) => null | GridActionColumnActions[];

  @Input() getCalculatedColumnTooltipMap?: ({
    rowData
  }: {
    rowData: any;
  }) => null | Partial<{ [key in GridActionColumnActions]: string }>;

  // This is identical to the 'additionalQueryData' property below. However, this allows query data to
  // be passed into a grid from an external source. The suppliedQueryData is then merged with the additionalQueryData
  // and eventually, the additionalQueryData is merged with the resultant queryObject.
  @Input() suppliedQueryData?: { [key: string]: any };

  // Override additional query data, completely overrides any additional query data set. Supplied
  // Query data, on the other hand, is merged into the additional query data;
  @Input() overrideAdditionalQueryData?: { [key: string]: any };

  // By default we're omitting group by data on count queries. When counting with group by this returns
  // a count per group of data.
  @Input() omitGroupByOnCount: boolean = true;

  // Columns to hide by default when the grid first loads. Columns can still be shown
  // via the column tool panel. Values should be field names (e.g. ['isExempt']).
  @Input() defaultHiddenColumns?: string[];

  // Columns to remove entirely from the grid (not available in the column tool panel).
  // Values should be field names (e.g. ['isExempt']).
  @Input() suppressedColumns?: string[];

  // Can supply a Filter Model to the grid to default it to a filtered state.
  @Input() FilterModel?: FilterModel;

  // Uses distinct aggregate function on query.
  @Input() preventDistinctCount: boolean = false;

  // Allows us to dynamically set row id's.
  @Input() getRowId?: (params: GetRowIdParams) => string;

  // If this is set to true, it will clear all filters currently set by the grid. We then set this to false
  // after the filters have been cleared.
  @Input() resetFilterModel: boolean = false;
  @Output() resetFilterModelChange = new EventEmitter<boolean>();

  // Emits a filter change event when filtering on grid changes.
  @Output() filterChangeEvent = new EventEmitter<boolean>();

  // This is used to reload the grid. Setting to true will trigger a reload, after which this variable
  // is set to false.
  @Input() reloadGridData: boolean = false;
  @Output() reloadGridDataChange = new EventEmitter<boolean>();

  // Emit action column event when an action icon is clicked.
  @Output() actionEvent: EventEmitter<{
    action: GridActionColumnActions;
    rowData: any;
    rowNode: IRowNode;
    cellRendererParams: ICellRendererParams;
  }> = new EventEmitter<{
    action: GridActionColumnActions;
    rowData: any;
    rowNode: IRowNode;
    cellRendererParams: ICellRendererParams;
  }>();

  // This represents that any selection has changed state.
  @Output() selectionChanged = new EventEmitter<SelectionChangedEvent>();
  // This represents that a row selection has changed state.
  @Output() rowSelectionChanged = new EventEmitter<RowSelectedEvent>();
  // This represents a row click event.
  @Output() rowClick = new EventEmitter<{ data: any }>();
  // When the grid data query is constructed, the query is emitted from the grid for any parent components to
  // receive.
  @Output() dataGridQueryChanged = new EventEmitter<{
    GridQuery: GenericObject;
    GridRequest: IServerSideGetRowsRequest;
  }>();
  // When the grid count query is constructed, the query is emitted from the grid for any parent components to
  // receive.
  @Output() countGridQueryChanged = new EventEmitter<{
    GridQuery: GenericObject;
    GridRequest: IServerSideGetRowsRequest;
  }>();

  @Output() resetAllResultFilters = new EventEmitter<boolean>();

  // Toggle this to hide the grid. Primarily used to reconstruct the grid after state change.
  showGrid: boolean = true;
  initialState?: GridState;
  totalRowCount: number = 0;
  GridApi?: GridApi;
  // Used to determine how many refreshes to perform on a grid. Used for events that
  // trigger multiple refreshes but only one refresh can happen at any given time.
  refreshCounter: number = 0;

  GetContextMenuItems: GetContextMenuItems = (): (
    MenuItemDef | DefaultMenuItem
  )[] => [
    'autoSizeAll',
    'expandAll',
    'contractAll',
    'copy',
    'copyWithHeaders',
    'copyWithGroupHeaders',
    'cut',
    'paste',
    'resetColumns',
    'chartRange',
    'pivotChart'
  ];

  // When attempting to export all rows for a grid configured with server-side pagination,
  // we set this variable to 'excel' or 'csv' (depending on which export is being performed).
  // We proceed to load all rows for the grid without pagination enabled, check to see if this variable
  // is set, then process the download after the data is loaded. After which, pagination is re-enabled
  // and this variable is reset to a null state.
  exportAfterDataReload: 'excel' | 'csv' | null = null;

  // We use this to signify if all data should be loaded for a grid (prevents pagination and loads all columns).
  // AG Grid has a pagination property, however enabling this forces manual pagination (through a control toolbar)
  // an prevents infinite scrolling.
  loadAllData: boolean = false;

  // This ignores pagination only. Similar to the loadAllData property above, however it doesn't load all column
  loadAllRows: boolean = false;

  // Can set additional query data to be merged into the resultant query object.
  // Useful for supplying additional table/column/optional data to a query perhaps
  // for logic on the backend reliant on a subquery.
  additionalQueryData?: { [key: string]: any } = {
    attributes: ['id']
  };

  // This is the data object used to represent the merged additionalQueryData and suppliedQueryData
  // objects. We use this object in order to preserve the original state of the additionalQueryData
  // and suppliedQueryData.
  mergedQueryData?: { [key: string]: any } = {};

  ServerSideDataSource?: {
    getRows: (params: IServerSideGetRowsParams) => void;
  };
  CurrentGridStateDataQuery?: GenericObject;
  CurrentGridStateCountQuery?: GenericObject;

  public gridState: {
    attributes: string[];
    filters: object[];
    sorters: object[];
  } = { attributes: [], filters: [], sorters: [] };

  // Universal search text is applied against all columns as a filter.
  UniversalSearch: { text: string } = { text: '' };

  // True when filters are active but the grid returned zero rows (overlay stays hidden).
  showFilteredEmptyGrid: boolean = false;

  constructor() {}
}
